import { PlusOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { useBasicDndDragger } from '@framework/ui/core/dnd/hooks/useBasicDndDragger.ts';
import { Button } from 'antd';
import styled from 'styled-components';
import { type FieldDatatype } from '../../../datatype/Datatype.ts';
import { type IReference } from '../../../references/IReference.ts';
import { AssignableExpressionInput } from '../../assignable/input/AssignableExpressionInput.tsx';
import { type ISwitchExpressionCase } from '../ISwitchExpression.ts';
import { SwitchExpressionCaseSchema } from '../SwitchExpressionSchema.ts';
import { SortableSwitchExpressionItem } from './SortableSwitchExpressionItem.tsx';

const SwitchExpressionInputWrapper = styled.div`
  border: 1px solid ${props => props.theme.colorBorder};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 16px;
`;

export interface ISwitchExpressionInputProps {
  name: string;
  fieldRef: FieldDatatype;
  references: IReference[];
}

export function SwitchExpressionInput(props: ISwitchExpressionInputProps) {
  const { name, fieldRef, references } = props;

  const editor = useRelationEditor<ISwitchExpressionCase>(`${name}.cases`);
  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  return (
    <SwitchExpressionInputWrapper>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {editor.items.map((item, index) => (
            <SortableSwitchExpressionItem
              active={active}
              key={item.__uid ?? '<unknown>'}
              uid={item.__uid ?? '<unknown>'}
              name={name}
              index={index}
              references={references}
              fieldRef={fieldRef}
              onRemove={() => {
                editor.removeAt(index);
              }}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        block
        onClick={() => {
          editor.append(SwitchExpressionCaseSchema.cast({}, { assert: false }));
        }}
        icon={<PlusOutlined />}
        type="primary"
        size="large"
      >
        Aggiungi condizione
      </Button>
      <div>
        <VerticalSpace size={12} />
        Se nessuna condizione viene rispettata:
        <VerticalSpace size={12} />
        <FormItem
          name={`${name}.defaultValue`}
          label="Valore di default"
          layout={{ removeMargin: true }}
        >
          <AssignableExpressionInput
            size="large"
            block
            references={references}
            name={`${name}.defaultValue`}
            field={fieldRef}
          />
        </FormItem>
      </div>
    </SwitchExpressionInputWrapper>
  );
}
